<template>
  <b-server-table
    :filter-props="filterProps"
    :headers="incidentSettlementListHeaders"
    :request-func="
      query =>
        taskList({
          ...query,
          typeId: 6,
        })
    "
    @click:row="openTask">
    <template #action-button>
      <v-tooltip :text="settlementTooltipText" location="bottom">
        <template #activator="{ props }">
          <span v-bind="props">
            <v-btn
              border
              color="white"
              density="comfortable"
              icon="mdi-information-outline"
              readonly></v-btn>
          </span>
        </template>
      </v-tooltip>
    </template>

    <template #item.incident="{ item }">
      <div class="d-flex flex-column">
        <span class="font-weight-bold">
          {{ item.entity?.incident?.number || '---' }}
        </span>
        <v-chip
          border
          class="font-weight-bold"
          density="comfortable"
          rounded="lg"
          size="small"
          variant="flat"
          style="width: max-content"
          :style="getIncidentStatusChipStyle(item.entity?.incident?.statusId)">
          {{ item.entity?.incident?.status?.name || '---' }}
        </v-chip>
      </div>
    </template>

    <template #item.task="{ item }">
      <div class="d-flex flex-column">
        <div
          class="font-weight-medium"
          :class="{
            'text-medium-emphasis text-decoration-line-through': isTaskDone(
              item.statusId
            ),
          }">
          {{ item.topic || '---' }}
        </div>
        <div class="font-size-12 text-medium-emphasis">
          {{ item?.description || '---' }}
        </div>
      </div>
    </template>

    <template #item.executor="{ item }">
      <span class="font-weight-medium">{{
        getPersonName(item.executor, { initials: true }) || '---'
      }}</span>
    </template>

    <template #item.author="{ item }">
      <span class="font-weight-medium">{{
        getPersonName(item.author, { initials: true }) || '---'
      }}</span>
    </template>

    <template #item.created_at="{ item }">
      <span v-if="item.created_at">
        {{
          new CustomDate(item.created_at).toString({
            time: true,
            format: 'locale-numeric',
          })
        }}
      </span>
      <span v-else>---</span>
    </template>

    <template #item.deadline="{ item }">
      <span
        v-if="item.deadline"
        class="font-weight-bold text-deep-orange-darken-3">
        {{
          new CustomDate(item.deadline).toString({
            time: true,
            format: 'locale-numeric',
          })
        }}
      </span>
      <span v-else>---</span>
    </template>

    <template #item.taskStatus="{ item }">
      <v-chip
        v-if="item.statusId"
        :style="getSettlementTaskStatusChipStyle(item.statusId)"
        border
        class="font-weight-bold"
        density="comfortable"
        rounded="lg"
        size="small"
        variant="flat">
        {{ item.status?.name?.toUpperCase() }}
      </v-chip>
      <span v-else>---</span>
    </template>
  </b-server-table>
</template>

<script lang="ts" setup>
import { useRouter } from 'vue-router'
import { BServerTable } from 'best-modules/components'
import { CustomDate } from 'best-modules/utils'
import { incidentSettlementListHeaders } from '@/utils/tableHeaders'
import { taskList } from '@/requests/task'
import { getPersonName } from 'best-modules/utils/helpers'
import { getIncidentStatusChipStyle } from '@/pages/operation-risk/incidents/constants'

defineOptions({
  name: 'SettlementList',
})

type SettlementListItem = {
  id: number
  incidentNumber: string | null
  incidentName: string | null
  task: string | null
  executor: string | null
  deadline: string | null
  taskStatusId: number | null
  taskStatus: string | null
}

const SETTLEMENT_TASK_STATUS_IDS = {
  DONE: 3,
  FINAL_DONE: 5,
} as const

const router = useRouter()

const settlementTooltipText = [
  'Врегулювання інцидентів - тут зібрані всі завдання,',
  'які були поставлені по інцидентах.',
  'Натисніть на рядок щоб відкрити завдання.',
].join(' ')

const filterProps = {
  searchPlaceholder: 'Пошук за завданням, виконавцем...',
  filterMap: [{ key: 'search' }],
}

const getSettlementTaskStatusChipStyle = (statusId: number | null) => {
  switch (statusId) {
    case 2:
      return {
        backgroundColor: '#eaf4ff',
        color: '#1565a9',
      }

    case 3:
    case 5:
      return {
        backgroundColor: '#e9f8f0',
        color: '#187a4d',
      }

    case 4:
      return {
        backgroundColor: '#fff1f1',
        color: '#b42318',
      }

    default:
      return {
        backgroundColor: '#fff8e8',
        color: '#b66f00',
      }
  }
}

const isTaskDone = (statusId: number | null) => {
  return (
    statusId === SETTLEMENT_TASK_STATUS_IDS.DONE ||
    statusId === SETTLEMENT_TASK_STATUS_IDS.FINAL_DONE
  )
}

const openTask = (settlement: SettlementListItem) => {
  router.push({
    name: 'single-task',
    params: { id: settlement.id },
  })
}
</script>
